import { useCallback, useMemo } from "react";
import {
    useAgentRunControls,
    useAgentRuns,
    useOrganizationAgentRuns,
} from "@api/agentRun.api";
import { useIsOrganizationChat, useSendMessage } from "@api/chat.api";
import { AgentRunWaitKind } from "@common/types/agentRun";
import { MessageType } from "@common/types/chat";
import { useStartCloseAgent } from "@components/commands/useStartCloseAgent";
import { Button } from "@design-system/Button/Button";
import { Link } from "@design-system/Link/Link";
import { Loader } from "@design-system/Loader/Loader";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { Tooltip } from "@design-system/Tooltip/Tooltip";
import { VStack } from "@design-system/VStack/VStack";
import { useViewOnlyRefusal } from "@hooks/permissions/useViewOnlyRefusal";
import { PlayIcon } from "@icons";
import { SubScopeProvider } from "@state/subScopeContext";
import { ChatDrawerHeader } from "../ChatDrawerHeader";
import { MessageForm, MessageFormSubmitParams } from "../MessageForm";
import { AgentRunRow, PROPOSAL_WAIT_TOOLTIP } from "./AgentRunRow";
import {
    FINISHED_KINDS,
    getAgentRunStatusKind,
    isWaitingOnProposalsOnly,
    CONTINUABLE_KINDS,
} from "./agentRunStatus";
import styles from "./AgentRunsPanel.module.scss";

const NO_REPLY_TARGET = "No run is waiting for a reply";
const PROPOSAL_ONLY_TARGET = "Approve or decline the proposal on the task";

/**
 * The Agent tab: every run newest first, with its controls. In a workspace
 * the workspace's runs plus Start, the footer shortcut and the reply
 * composer; in organization chat every visible client's runs, each row
 * sub-scoped to its workspace, with no Start (it needs one workspace's close
 * picker) and no composer (it targets one workspace thread).
 */
export const AgentRunsPanel = () => {
    const isOrganizationChat = useIsOrganizationChat();
    const workspaceQuery = useAgentRuns({ enabled: !isOrganizationChat });
    const organizationQuery = useOrganizationAgentRuns({
        enabled: isOrganizationChat,
    });
    const { data, status, refetch } = isOrganizationChat
        ? organizationQuery
        : workspaceQuery;
    const { isReady, startFromPicker } = useStartCloseAgent();
    const { refuseIfViewOnly } = useViewOnlyRefusal();
    const toaster = useToaster();
    const controls = useAgentRunControls();
    const { mutateAsync: sendMessage } = useSendMessage();
    const runs = useMemo(() => data?.runs ?? [], [data]);

    const start = useCallback(() => {
        if (refuseIfViewOnly()) {
            return;
        }
        startFromPicker();
    }, [refuseIfViewOnly, startFromPicker]);

    const newestActive = runs.find(
        (run) => !FINISHED_KINDS.has(getAgentRunStatusKind(run)),
    );
    // The composer posts to the newest waiting run's first answer or status
    // wait; a proposal wait takes approvals, not free text.
    const waitingRun = runs.find(
        (run) => getAgentRunStatusKind(run) === "waiting",
    );
    const replyWait =
        waitingRun?.waitingOn.find(
            (wait) => wait.kind !== AgentRunWaitKind.proposal,
        ) ?? null;
    const replyThreadId = replyWait?.threadId ?? null;
    const disabledReason =
        waitingRun === undefined ? NO_REPLY_TARGET : PROPOSAL_ONLY_TARGET;

    const reply = useCallback(
        async ({ content }: MessageFormSubmitParams) => {
            if (replyThreadId === null) {
                return;
            }
            try {
                await sendMessage({
                    threadId: replyThreadId,
                    content,
                    type: MessageType.SLATE,
                });
            } catch (error) {
                // Re-throw so MessageForm keeps the draft for a retry.
                toaster.error("Failed to send message. Please try again.");
                throw error;
            }
        },
        [replyThreadId, sendMessage, toaster],
    );

    const renderReplyTarget = () => {
        if (waitingRun === undefined || replyWait === null) {
            return null;
        }
        const entityName = waitingRun.entity?.name ?? "entity";
        return (
            <span className={styles.composerTarget}>
                Replying to{" "}
                {replyWait.title === null
                    ? entityName
                    : `${entityName} · ${replyWait.title}`}
            </span>
        );
    };

    const renderList = () => {
        if (status === "pending") {
            return (
                <div className={styles.state}>
                    <Loader />
                </div>
            );
        }
        if (status === "error") {
            return (
                <VStack gap={8} align="center" className={styles.state}>
                    <span>Failed to load agent runs</span>
                    <Button
                        size="sm"
                        variant="primary"
                        onClick={() => refetch()}
                    >
                        Retry
                    </Button>
                </VStack>
            );
        }
        if (runs.length === 0) {
            return (
                <VStack gap={8} align="center" className={styles.state}>
                    <span>No agent runs yet</span>
                    {isOrganizationChat ? null : (
                        <Button
                            size="sm"
                            variant="secondary"
                            leftIcon={<PlayIcon />}
                            onClick={start}
                            disabled={!isReady}
                        >
                            Start
                        </Button>
                    )}
                </VStack>
            );
        }
        return runs.map((run) => (
            <SubScopeProvider
                key={run.id}
                value={{ workspaceId: run.workspaceId }}
            >
                <AgentRunRow
                    run={run}
                    isOrganizationChat={isOrganizationChat}
                />
            </SubScopeProvider>
        ));
    };

    const renderFooter = () => {
        if (newestActive === undefined) {
            return null;
        }
        return (
            <VStack gap={8} align="start" className={styles.footer}>
                {CONTINUABLE_KINDS.has(getAgentRunStatusKind(newestActive)) ? (
                    <Tooltip
                        content={
                            isWaitingOnProposalsOnly(newestActive)
                                ? PROPOSAL_WAIT_TOOLTIP
                                : undefined
                        }
                    >
                        <Link
                            size="xs"
                            className={styles.footerAction}
                            disabled={
                                controls.isBusy ||
                                isWaitingOnProposalsOnly(newestActive)
                            }
                            onClick={() => controls.onContinue(newestActive.id)}
                        >
                            Continue agent
                        </Link>
                    </Tooltip>
                ) : (
                    <Link
                        size="xs"
                        className={styles.footerAction}
                        disabled={controls.isBusy}
                        onClick={() => controls.onPause(newestActive.id)}
                    >
                        Pause agent
                    </Link>
                )}
            </VStack>
        );
    };

    return (
        <>
            <ChatDrawerHeader />
            <div className={styles.panel}>
                <VStack gap={12} className={styles.list}>
                    {renderList()}
                </VStack>
                {isOrganizationChat ? null : (
                    <>
                        {renderFooter()}
                        <Tooltip
                            content={
                                replyThreadId === null
                                    ? disabledReason
                                    : undefined
                            }
                        >
                            <VStack gap={4} className={styles.composer}>
                                {renderReplyTarget()}
                                <MessageForm
                                    placeholder="Chat with Agent"
                                    hideOrganizationScopePicker
                                    disabled={replyThreadId === null}
                                    resetKey={replyThreadId ?? "agent"}
                                    onSubmit={reply}
                                />
                            </VStack>
                        </Tooltip>
                    </>
                )}
            </div>
        </>
    );
};
